import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import Loader from './Loader';
import useResizeObserver from 'utils/useResizeObserver';

const POPUP_OFFSET = 12;

const AutocompletePopup = ({ editorContainerEl }) => {
  const [autocompletePosition, setAutocompletePosition] = useState(null);
  const [arrowPosition, setArrowPosition] = useState(null);

  /* Selectors */
  const isOpen = useSelector((state) => state.autocomplete.isOpen);
  const containerData = useSelector((state) => state.autocomplete.containerData);
  const isLoading = useSelector((state) => state.autocomplete.isLoading);
  const streamType = useSelector((state) => state.autocomplete.streamType);
  const isWriting = useSelector((state) => state.autocomplete.isWriting);
  
  const displayText = useSelector((state) => {
    return state.autocomplete.textSuggestion || state.autocomplete.nextwords;
  });

  const [ref, size] = useResizeObserver(50);

  useEffect(() => {
    const width = size.width;

    if (containerData && size.width && size.height) {
      let x = containerData.left - (width/2 - POPUP_OFFSET);
      // Check left edge
      x = x > POPUP_OFFSET ? x : -POPUP_OFFSET;

      // Check right edge
      x = x + width > editorContainerEl.clientWidth
        ? editorContainerEl.clientWidth - width + POPUP_OFFSET
        : x;

      let y = containerData.top - size.height + 1;

      setAutocompletePosition({
        x, y,
      });

      let xArrow = containerData.left > editorContainerEl.clientWidth
        ? editorContainerEl.clientWidth
        : containerData.left;
      
      setArrowPosition({
        x: xArrow,
        y: containerData.top
      });
    }
  }, [containerData, editorContainerEl.clientWidth, size]);

  return (
    <>
      <Arrow
        position={arrowPosition}
        isOpen={isOpen}
      />
      <Text
        ref={ref}
        position={autocompletePosition}
        isOpen={isOpen}
      >
        {displayText}
        {isLoading && (
            <>
              {`completing ${streamType} `}
              <Loader />
            </>
          )}
        {!isWriting && (<Hint
          data-tooltip-id='autocomplete-tab'
        >
          tab
        </Hint>)}
      </Text>
    </>
  );
}

export default AutocompletePopup;

const Text = styled.div`
  position: absolute;
  ${p => !p.position ? css`
    display: none;
  ` : css`
    left: 0;
    top: 0;
    transform: translateY(${p => p.position?.y !== undefined && p.position.y}px)
      translateX(${p => p.position?.x !== undefined && p.position?.x}px);
  
  `}

  background-color: var(--lizzy-white);
  z-index: 11;
  font-size: 14px;
  padding: 2px 4px;
  border: 1px solid var(--lizzy-black-10);
  border-radius: 4px;
  color: var(--lizzy-black-35);
  max-width: calc(100% + 36px);
  white-space: normal;
  filter: drop-shadow(0px -10px 27px rgba(0, 0, 0, 0.09))
    drop-shadow(0px 3.75998px 11.28px rgba(0, 0, 0, 0.0503198))
    drop-shadow(0px 2.01027px 6.0308px rgba(0, 0, 0, 0.0417275))
    drop-shadow(0px 1.12694px 3.38082px rgba(0, 0, 0, 0.035))
    drop-shadow(0px 0.598509px 1.79553px rgba(0, 0, 0, 0.0282725))
    drop-shadow(0px 0.249053px 0.747159px rgba(0, 0, 0, 0.0196802));

  display: ${p => p.isOpen ? 'inline-block' : 'none'};
`;

const Arrow = styled.div`
  position: absolute;

  ${p => !p.position && css`
    display: none;
  `}

  left: ${p => p.position?.x || 0}px;

  top: ${p => p.position?.y || 0}px;
  margin-left: -6px;
  z-index: 12;
  display: ${p => p.isOpen ? 'block' : 'none'};

  &::before, &::after {
    position: absolute;
    content: '';
    width: 0; 
    height: 0; 
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid var(--lizzy-white);
    bottom: -5px;
    z-index: 2;
  }

  &::after {
    border-top-color: var(--lizzy-black-10);
    z-index: 1;
    bottom: -6px;
  }
`

const Hint = styled.span`
  display: inline-block;
  line-height: 13px;
  border: 1px solid var(--lizzy-black-35);
  font-size: 10px;
  border-radius: 4px;
  padding: 2px 7px;
  margin-left: 7px;
  transform: translateY(-2px);
  position: relative;
  z-index: 10;
  cursor: default;
`;